import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faRepeat, faShuffle } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  faBackwardStep,
  faForwardStep,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { useCurrentListeningParty } from '@hooks/usePlayButton';
import { toNextTrack, toPrevTrack, useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { cycleRepeatMode, RepeatMode } from '@store/audio/AudioMeta';
import { toggleShuffleEnabled } from '@store/audio/AudioQueue';
import { FlexBetweenWrapper } from '../shared';

export const AudioController = () => {
  const {
    isNextTrackDisabled,
    playing,
    loading,
    loadingActiveTrack,
    repeatMode,
    shuffleEnabled,
    disableNextPrev,
    activeTrackReleaseInfo,
  } = useAudioController();

  const currentListeningParty = useCurrentListeningParty({
    releaseId: activeTrackReleaseInfo?.id,
    listeningParty: activeTrackReleaseInfo?.listeningParty,
  });

  return (
    <FlexBetweenWrapper>
      <StateIconButton
        data-state={shuffleEnabled ? 'active' : 'inactive'}
        aria-label="Shuffle"
        variant="inverted"
        icon={<FontAwesomeIcon icon={faShuffle} />}
        onClick={toggleShuffleEnabled}
        iconSize={24}
      />
      <Wrapper>
        <StyledIconButton
          aria-label="Previous Track"
          variant="inverted"
          icon={<FontAwesomeIcon icon={faBackwardStep} />}
          disabled={disableNextPrev}
          onClick={e => {
            e.stopPropagation();
            toPrevTrack();
          }}
          iconSize={32}
          size={34}
          animate
        />
        <PlayPauseWrapper>
          <PlayButton
            playing={playing}
            loading={loading || loadingActiveTrack}
            onClick={togglePlayPause}
            size="l"
            color="white"
            rounded
            currentListeningParty={currentListeningParty}
          />
        </PlayPauseWrapper>
        <StyledIconButton
          aria-label="Next Track"
          variant="inverted"
          icon={<FontAwesomeIcon icon={faForwardStep} />}
          disabled={isNextTrackDisabled || disableNextPrev}
          onClick={toNextTrack}
          iconSize={32}
          size={34}
          animate
        />
      </Wrapper>
      <StateIconButton
        data-state={repeatMode !== RepeatMode.NO_REPEAT ? 'active' : 'inactive'}
        aria-label="Repeat"
        variant="inverted"
        icon={
          <RepeatWrapper>
            <FontAwesomeIcon icon={faRepeat} />
            {repeatMode === RepeatMode.REPEAT_ONE && (
              <Bubble>
                <span>1</span>
              </Bubble>
            )}
          </RepeatWrapper>
        }
        onClick={cycleRepeatMode}
        iconSize={24}
      />
    </FlexBetweenWrapper>
  );
};

const PlayPauseWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  size: 64,
  '.play-icon': {
    size: 32,
  },

  '.pause-icon': {
    size: 32,
  },

  '.play-button-container': {
    width: 64,
    height: 64,
  },
});

const StyledIconButton = styled(IconButton, {
  '&:hover, &:focus, &:active': {
    backgroundColor: 'transparent !important',
  },
});

const StateIconButton = styled(IconButton, {
  '&:hover, &:focus, &:active': {
    backgroundColor: 'transparent !important',
  },
  '&[data-state=active]': {
    color: '$fuchsia500 !important',
  },
  '&[data-state=inactive]': {
    color: '$white !important',
  },
});

const Wrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 44,
});

const RepeatWrapper = styled('div', {
  position: 'relative',
});

const Bubble = styled('div', {
  position: 'absolute',
  backgroundColor: '$white',
  borderRadius: '50%',
  height: 12,
  width: 12,
  fontFamily: '$base',
  fontSize: 8,
  lineHeight: '10px',
  fontWeight: 900,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  top: 0,
  right: 0,
  color: '$black',
});
